// Excel / CSV helpers for bulk notification import.
// Times are plain local (IST) wall-clock times, exactly like the Create Notification form:
// they are sent as "YYYY-MM-DD HH:mm:ss" with no timezone conversion.

export const SEND_TO = [
  { label: "All", app: 0 },
  { label: "Customer", app: 1 },
  { label: "Provider", app: 2 },
  { label: "Admin & Support", app: 3 },
];

export const MAX_ROWS = 500;
const TITLE_MAX = 256;
const MESSAGE_MAX = 1024;
const HEADERS = ["Date", "Time", "Title", "Message", "Send to"];

const loadExcelJS = async () => (await import("exceljs")).default;

const pad = (n) => String(n).padStart(2, "0");
const norm = (s) => String(s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

const SEND_TO_ALIASES = {
  all: 0,
  customer: 1,
  customers: 1,
  provider: 2,
  providers: 2,
  podian: 2,
  podians: 2,
  adminsupport: 3,
  adminandsupport: 3,
  admin: 3,
};

export const sendToLabel = (app) =>
  SEND_TO.find((s) => s.app === app)?.label ?? "";

// ---------------------------------------------------------------- template

export async function downloadTemplate() {
  const ExcelJS = await loadExcelJS();
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Notifications", {
    views: [{ state: "frozen", ySplit: 1 }],
  });

  ws.columns = [
    { header: "Date", key: "date", width: 14, style: { numFmt: "@" } },
    { header: "Time", key: "time", width: 10, style: { numFmt: "@" } },
    { header: "Title", key: "title", width: 32 },
    { header: "Message", key: "message", width: 60 },
    { header: "Send to", key: "sendTo", width: 18 },
  ];
  ws.getRow(1).font = { bold: true };
  ws.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF8AF41" },
  };

  const list = `"${SEND_TO.map((s) => s.label).join(",")}"`;
  for (let r = 2; r <= MAX_ROWS + 1; r++) {
    ws.getCell(`E${r}`).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [list],
      showErrorMessage: true,
      errorTitle: "Send to",
      error: "Choose All, Customer, Provider or Admin & Support",
    };
  }

  const info = wb.addWorksheet("Instructions");
  info.columns = [
    { header: "Column", key: "c", width: 12 },
    { header: "How to fill", key: "h", width: 90 },
  ];
  info.getRow(1).font = { bold: true };
  [
    ["Date", "YYYY-MM-DD (e.g. 2026-11-01). DD/MM/YYYY and Excel date cells also work."],
    ["Time", "24-hour IST time HH:mm (e.g. 18:30). 6:30 PM and Excel time cells also work."],
    ["Title", `Required, max ${TITLE_MAX} characters.`],
    ["Message", `Required, max ${MESSAGE_MAX} characters. A message starting with http is sent as "Tap here to see image."`],
    ["Send to", "All, Customer, Provider or Admin & Support (pick from the dropdown)."],
    ["", `Date & time must be in the future. Max ${MAX_ROWS} rows per file. Several notifications can have the same time.`],
  ].forEach(([c, h]) => info.addRow({ c, h }));
  info.addRow({});
  info.addRow({ c: "Example", h: "2026-11-01 | 10:30 | Diwali offer | 20% off on all shoots today | Customer" });
  info.getRow(info.rowCount).font = { italic: true };

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "notification-import-template.xlsx";
  a.click();
  URL.revokeObjectURL(a.href);
}

// ---------------------------------------------------------------- reading

function cellToPlain(v) {
  if (v === null || v === undefined) return "";
  if (v instanceof Date) return v;
  if (typeof v === "object") {
    if (Array.isArray(v.richText)) return v.richText.map((t) => t.text).join("");
    if ("result" in v) return cellToPlain(v.result);
    if ("text" in v) return cellToPlain(v.text);
    if ("hyperlink" in v) return v.hyperlink;
    return "";
  }
  return v;
}

async function readXlsx(file) {
  const ExcelJS = await loadExcelJS();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  const ws = wb.getWorksheet("Notifications") || wb.worksheets[0];
  if (!ws) return [];
  const rows = [];
  ws.eachRow({ includeEmpty: false }, (row) => {
    const values = [];
    for (let c = 1; c <= row.cellCount; c++) {
      values.push(cellToPlain(row.getCell(c).value));
    }
    rows.push(values);
  });
  return rows;
}

function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  text = text.replace(/^﻿/, "");
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += ch;
    }
  }
  if (field !== "" || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

// ---------------------------------------------------------------- parsing values

function parseDate(v) {
  if (v instanceof Date) {
    return { y: v.getUTCFullYear(), m: v.getUTCMonth() + 1, d: v.getUTCDate() };
  }
  if (typeof v === "number") {
    const dt = new Date(Date.UTC(1899, 11, 30) + Math.floor(v) * 86400000);
    return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
  }
  const s = String(v).trim();
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (m) return { y: +m[1], m: +m[2], d: +m[3] };
  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})/);
  if (m) return { y: +m[3], m: +m[2], d: +m[1] };
  return null;
}

function parseTime(v) {
  if (v instanceof Date) {
    return { h: v.getUTCHours(), mi: v.getUTCMinutes() };
  }
  if (typeof v === "number") {
    const minutes = Math.round((v - Math.floor(v)) * 1440) % 1440;
    return { h: Math.floor(minutes / 60), mi: minutes % 60 };
  }
  const s = String(v).trim().toLowerCase();
  const m = s.match(/^(\d{1,2})(?::(\d{2}))?(?::\d{2})?\s*(am|pm)?$/);
  if (!m || (!m[2] && !m[3])) return null;
  let h = +m[1];
  const mi = m[2] ? +m[2] : 0;
  if (m[3]) {
    if (h < 1 || h > 12) return null;
    if (m[3] === "pm" && h !== 12) h += 12;
    if (m[3] === "am" && h === 12) h = 0;
  }
  return { h, mi };
}

function isRealDate({ y, m, d }) {
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

function parseSendTo(v) {
  if (typeof v === "number" && [0, 1, 2, 3].includes(v)) return v;
  const key = norm(v);
  if (/^[0-3]$/.test(key)) return +key;
  return key in SEND_TO_ALIASES ? SEND_TO_ALIASES[key] : null;
}

// ---------------------------------------------------------------- main

/**
 * Reads an .xlsx or .csv file and returns { rows, error }.
 * Each row: { row, title, message, app, date_time, display, errors: [] }.
 */
export async function readNotificationFile(file) {
  const name = file.name.toLowerCase();
  let raw;
  if (name.endsWith(".csv")) {
    raw = parseCsv(await file.text());
  } else if (name.endsWith(".xlsx")) {
    raw = await readXlsx(file);
  } else {
    return { rows: [], error: "Please upload an .xlsx or .csv file." };
  }

  if (!raw.length) return { rows: [], error: "The file is empty." };

  const header = raw[0].map((h) => norm(h));
  const idx = {
    date: header.indexOf("date"),
    time: header.indexOf("time"),
    title: header.indexOf("title"),
    message: header.indexOf("message"),
    sendTo: header.indexOf("sendto"),
  };
  const missing = HEADERS.filter((h, i) => Object.values(idx)[i] === -1);
  if (missing.length) {
    return {
      rows: [],
      error: `Missing column(s): ${missing.join(", ")}. Please use the template.`,
    };
  }

  const body = raw
    .slice(1)
    .filter((r) => r.some((c) => String(cellToPlain(c)).trim() !== ""));

  if (!body.length) return { rows: [], error: "No notification rows found." };
  if (body.length > MAX_ROWS) {
    return {
      rows: [],
      error: `The file has ${body.length} rows. Maximum ${MAX_ROWS} per import.`,
    };
  }

  const now = new Date();
  const rows = body.map((r, i) => {
    const get = (k) => cellToPlain(r[idx[k]]);
    const errors = [];
    const title = String(get("title")).trim();
    const message = String(get("message")).trim();
    const app = parseSendTo(get("sendTo"));

    const dateCell = get("date");
    const timeCell = get("time");
    const date = dateCell === "" ? null : parseDate(dateCell);
    // A full date-time typed in the Date column is accepted when Time is empty.
    const time =
      timeCell === ""
        ? dateCell instanceof Date
          ? parseTime(dateCell)
          : null
        : parseTime(timeCell);

    if (!title) errors.push("Title is required");
    else if (title.length > TITLE_MAX) errors.push(`Title max ${TITLE_MAX} characters`);
    if (!message) errors.push("Message is required");
    else if (message.length > MESSAGE_MAX) errors.push(`Message max ${MESSAGE_MAX} characters`);
    if (app === null) errors.push("Send to must be All, Customer, Provider or Admin & Support");

    let date_time = "";
    let display = "";
    if (!date || !isRealDate(date)) errors.push("Invalid date");
    if (!time || time.h > 23 || time.mi > 59) errors.push("Invalid time");
    if (date && isRealDate(date) && time && time.h <= 23 && time.mi <= 59) {
      const local = new Date(date.y, date.m - 1, date.d, time.h, time.mi, 0);
      date_time = `${date.y}-${pad(date.m)}-${pad(date.d)} ${pad(time.h)}:${pad(time.mi)}:00`;
      display = local;
      if (local <= now) errors.push("Date & time must be in the future");
    }

    return { row: i + 1, title, message, app, date_time, display, errors };
  });

  return { rows, error: "" };
}
